import { CloudState } from '../../store/cloud-types';
import { logger } from '@/utils/logger';
import {
    createFirestoreDeleteFieldSentinel,
    isFirestoreDeleteFieldSentinel,
    omitInlineDataUrlFields,
    sanitizeDocumentForFirestore,
} from '@/utils/firestore-sanitizer';
import type { ProjectMember } from '../auth/types';
import type { Personnel } from '../personnel/types.ts';
import {
    composePersonnelProfiles,
    PUBLIC_PERSONNEL_FIELDS,
    SENSITIVE_PERSONNEL_FIELDS,
    type PersonnelProfileDoc,
} from '../personnel/personnel-privacy';
import type { ProjectScope } from './project-scope';
import { createAndStoreProjectWriteIntent } from './project-write-operation-factory';
import {
    asArray,
    asString,
    toProjectMeta,
    toShowStructure,
} from './project-data-shape';
import {
    applyProjectMemberPersonnelLinks,
    enqueuePublicPersonnelProfileWrite,
    enqueueSensitivePersonnelProfileWrite,
    mergePersonnelServerLinks,
    type PersonnelProfilePatch,
} from './project-personnel-links';
import { isSameSerializableValue } from './serializable-comparison';
import { getProjectWriteBaseline } from './project-reader';

export {
    archiveProject,
    createCloudProject,
    deleteCloudProject,
    getCloudProject,
    getUserProjects,
    normalizeProjectCommunicationAudiences,
    subscribeToProject,
    unarchiveProject,
    updateProjectClosingDate,
    updateProjectCommunicationAudiences,
    updateProjectCommunicationSettings,
} from './project-catalog-api';
export type { CloudProjectMeta } from './project-catalog-api';

export {
    applyProjectMemberPersonnelLinks,
    mergePersonnelServerLinks,
} from './project-personnel-links';

const RESOURCE_PROPS = 'props';
const RESOURCE_SETS = 'sets';
const RESOURCE_SOUND = 'sound';
const RESOURCE_LIGHTING = 'lighting';
const RESOURCE_PROMPT_BOOK = 'promptBook';
const RESOURCE_FLY = 'fly';
const RESOURCE_PROJECTIONS = 'projections';

function comparableArrayEntity<T extends { id: string }>(item: T): Record<string, unknown> {
    const {
        updatedAtMs: _updatedAtMs,
        updatedBy: _updatedBy,
        ...rest
    } = item as T & { updatedAtMs?: unknown; updatedBy?: unknown };
    return rest;
}

function changedProfileFields(
    existing: PersonnelProfileDoc | undefined,
    next: PersonnelProfilePatch
): PersonnelProfilePatch | null {
    const patch: PersonnelProfilePatch = { id: next.id };
    for (const [key, value] of Object.entries(next)) {
        if (key === 'id') continue;
        if (isFirestoreDeleteFieldSentinel(value)) {
            if (existing && Object.hasOwn(existing, key)) {
                Object.assign(patch, { [key]: value });
            }
            continue;
        }
        if (!isSameSerializableValue(existing?.[key as keyof PersonnelProfileDoc], value)) {
            Object.assign(patch, { [key]: value });
        }
    }
    return Object.keys(patch).length > 1 ? patch : null;
}

function toPersonnelProfilePatch(
    personnel: Personnel,
    fields: readonly (keyof Personnel)[]
): PersonnelProfilePatch {
    const profile: PersonnelProfilePatch = { id: personnel.id };
    for (const field of fields) {
        if (field === 'id') continue;

        const value = personnel[field];
        if (value !== undefined) {
            Object.assign(profile, { [field]: value });
        } else if (Object.hasOwn(personnel, field)) {
            Object.assign(profile, { [field]: createFirestoreDeleteFieldSentinel() });
        }
    }
    return profile;
}

function toPublicPersonnelProfilePatch(personnel: Personnel): PersonnelProfilePatch {
    return toPersonnelProfilePatch(personnel, PUBLIC_PERSONNEL_FIELDS);
}

function toSensitivePersonnelProfilePatch(personnel: Personnel): PersonnelProfilePatch {
    return toPersonnelProfilePatch(personnel, SENSITIVE_PERSONNEL_FIELDS);
}

function toComparableShowStructure(value: unknown): Omit<CloudState['showStructure'], 'scriptBlocks'> {
    const showStructure = toShowStructure(value);
    return {
        acts: showStructure.acts,
        scenes: showStructure.scenes,
        characters: showStructure.characters,
        ...(showStructure.characterHints ? { characterHints: showStructure.characterHints } : {}),
    };
}

type ProjectWriteBaseline = Awaited<ReturnType<typeof getProjectWriteBaseline>>;

function projectSaveOptionalResources(localState: Partial<CloudState>): string[] {
    const shouldReadSound = localState.soundClips !== undefined
        || localState.soundCues !== undefined
        || localState.micAssignments !== undefined
        || localState.micSwaps !== undefined
        || localState.micPackConfig !== undefined;
    const shouldReadLighting = localState.followSpots !== undefined
        || localState.lightingCues !== undefined
        || localState.followSpotCues !== undefined;
    const shouldReadPromptBook = localState.cueAnchors !== undefined
        || localState.customCues !== undefined;

    return [
        ...(localState.props === undefined ? [] : [RESOURCE_PROPS]),
        ...(localState.sets === undefined ? [] : [RESOURCE_SETS]),
        ...(shouldReadSound ? [RESOURCE_SOUND] : []),
        ...(shouldReadLighting ? [RESOURCE_LIGHTING] : []),
        ...(shouldReadPromptBook ? [RESOURCE_PROMPT_BOOK] : []),
        ...(localState.flyCues === undefined ? [] : [RESOURCE_FLY]),
        ...(localState.projectionCues === undefined ? [] : [RESOURCE_PROJECTIONS]),
    ];
}

async function readProjectSaveBaseline(
    scope: ProjectScope,
    localState: Partial<CloudState>,
    actorId: string,
): Promise<ProjectWriteBaseline> {
    return getProjectWriteBaseline(scope, {
        actorUserId: actorId,
        includeCastAssignments: false,
        ...(localState.projectMeta === undefined ? {} : { requireProjectMeta: true }),
        optionalResources: projectSaveOptionalResources(localState),
    });
}

function projectSavePersonnel(localState: Partial<CloudState>, baseline: ProjectWriteBaseline): Personnel[] {
    return applyProjectMemberPersonnelLinks(
        mergePersonnelServerLinks(
            localState.personnel || [],
            composePersonnelProfiles(baseline.publicProfiles, baseline.sensitiveProfiles)
        ),
        baseline.members
    );
}

function projectResourceData(baseline: ProjectWriteBaseline, resourceId: string): Record<string, unknown> {
    return baseline.resources[resourceId] ?? {};
}

/**
 * Updates an existing project using a Transactional Union Merge approach
 */
export async function saveCloudProject(scope: ProjectScope, localState: Partial<CloudState>, userId: string, _sessionId: string | null = null): Promise<void> {
    try {
        const baseline = await readProjectSaveBaseline(scope, localState, userId);
        const personnelForSave = projectSavePersonnel(localState, baseline);

        await enqueueProjectStructureWrites(scope, userId, localState, baseline);
        await enqueueProjectPeopleAndMetaWrites(scope, userId, localState, baseline, personnelForSave);
        await enqueueDepartmentProjectDataWrites(scope, userId, localState, baseline);
        await enqueueRunSheetProjectDataWrites(scope, userId, localState, baseline);
        await enqueueProjectTitleIfChanged(scope, userId, localState, baseline);

        logger.cloud("Cloud save complete (Granular).");
    } catch (error: unknown) {
        logger.error("Error saving cloud project:", error);
        throw error;
    }
}

async function enqueueProjectStructureWrites(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    baseline: ProjectWriteBaseline,
): Promise<void> {
    await enqueueRowsReplacementWrite({
        scope,
        actorId,
        existingRows: asArray<CloudState['rows'][number]>(baseline.structure.rows),
        nextRows: localState.rows,
    });

    if (
        localState.showStructure !== undefined
        && !isSameSerializableValue(
            toComparableShowStructure(localState.showStructure),
            toComparableShowStructure(baseline.structure.showStructure),
        )
    ) {
        await enqueueScriptShowStructureReviewWrite(scope, actorId, localState.showStructure);
    }
}

async function enqueueProjectPeopleAndMetaWrites(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    baseline: ProjectWriteBaseline,
    personnelForSave: Personnel[],
): Promise<void> {
    await enqueuePeopleCastingEntityWrites({
        scope,
        actorId,
        existingPublicProfiles: baseline.publicProfiles,
        existingSensitiveProfiles: baseline.sensitiveProfiles,
        actorMember: baseline.actorMember,
        personnel: localState.personnel === undefined ? undefined : personnelForSave,
    });

    await enqueueProjectMetaSettingsWrite({
        scope,
        actorId,
        existingProjectMeta: toProjectMeta(baseline.meta.projectMeta, asString(baseline.root.title, '')),
        nextProjectMeta: localState.projectMeta,
    });
}

async function enqueueDepartmentProjectDataWrites(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    baseline: ProjectWriteBaseline,
): Promise<void> {
    // Project assets in this resource are legacy/snapshot-like. The live asset
    // library is user-scoped, so do not reintroduce project LWW writes here.
    await enqueuePropsProjectDataWrites(scope, actorId, localState, projectResourceData(baseline, RESOURCE_PROPS));
    await enqueueSetsProjectDataWrites(scope, actorId, localState, projectResourceData(baseline, RESOURCE_SETS));
    await enqueueSoundProjectDataWrites(scope, actorId, localState, projectResourceData(baseline, RESOURCE_SOUND));
    await enqueueLightingProjectDataWrites(scope, actorId, localState, projectResourceData(baseline, RESOURCE_LIGHTING));
    await enqueueProjectionProjectDataWrites(scope, actorId, localState, projectResourceData(baseline, RESOURCE_PROJECTIONS));
}

async function enqueuePropsProjectDataWrites(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    existingPropsData: Record<string, unknown>,
): Promise<void> {
    if (localState.props === undefined) {
        return;
    }

    await enqueueArrayEntityPatches({
        scope,
        actorId,
        feature: 'department_tracking',
        capability: 'EDIT_PROPS',
        conflictPrefix: 'props',
        existingItems: asArray<CloudState['props'][number]>(existingPropsData.props),
        nextItems: localState.props || [],
    });
}

async function enqueueSetsProjectDataWrites(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    existingSetsData: Record<string, unknown>,
): Promise<void> {
    if (localState.sets === undefined) {
        return;
    }

    await enqueueArrayEntityPatches({
        scope,
        actorId,
        feature: 'department_tracking',
        capability: 'EDIT_SETS',
        conflictPrefix: 'sets',
        existingItems: asArray<CloudState['sets'][number]>(existingSetsData.sets).map(setProjectWritePayload),
        nextItems: (localState.sets || []).map(setProjectWritePayload),
    });
}

async function enqueueSoundProjectDataWrites(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    existingSoundData: Record<string, unknown>,
): Promise<void> {
    await enqueueSoundEntityWrites(scope, actorId, localState, existingSoundData, 'soundClips', 'EDIT_SOUND');
    await enqueueSoundEntityWrites(scope, actorId, localState, existingSoundData, 'soundCues', 'EDIT_SOUND');
    await enqueueSoundEntityWrites(scope, actorId, localState, existingSoundData, 'micAssignments', 'EDIT_SOUND');
    await enqueueSoundEntityWrites(scope, actorId, localState, existingSoundData, 'micSwaps', 'EDIT_SOUND');
    await enqueueMicPackConfigWrite({
        scope,
        actorId,
        existingConfig: existingSoundData.micPackConfig,
        nextConfig: localState.micPackConfig,
    });
}

async function enqueueSoundEntityWrites<K extends 'soundClips' | 'soundCues' | 'micAssignments' | 'micSwaps'>(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    existingSoundData: Record<string, unknown>,
    field: K,
    capability: string,
): Promise<void> {
    const nextItems = localState[field];
    if (nextItems === undefined) {
        return;
    }

    await enqueueArrayEntityPatches({
        scope,
        actorId,
        feature: 'department_tracking',
        capability,
        conflictPrefix: field,
        existingItems: asArray<CloudState[K][number]>(existingSoundData[field]),
        nextItems: nextItems || [],
    });
}

async function enqueueLightingProjectDataWrites(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    existingLightingData: Record<string, unknown>,
): Promise<void> {
    await enqueueLightingEntityWrites(scope, actorId, localState, existingLightingData, 'followSpots');
    await enqueueLightingEntityWrites(scope, actorId, localState, existingLightingData, 'lightingCues');
    await enqueueLightingEntityWrites(scope, actorId, localState, existingLightingData, 'followSpotCues');
}

async function enqueueLightingEntityWrites<K extends 'followSpots' | 'lightingCues' | 'followSpotCues'>(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    existingLightingData: Record<string, unknown>,
    field: K,
): Promise<void> {
    const nextItems = localState[field];
    if (nextItems === undefined) {
        return;
    }

    await enqueueArrayEntityPatches({
        scope,
        actorId,
        feature: 'department_tracking',
        capability: 'EDIT_LIGHTING',
        conflictPrefix: field,
        existingItems: asArray<CloudState[K][number]>(existingLightingData[field]),
        nextItems: nextItems || [],
    });
}

async function enqueueProjectionProjectDataWrites(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    existingProjectionsData: Record<string, unknown>,
): Promise<void> {
    if (localState.projectionCues === undefined) {
        return;
    }

    await enqueueArrayEntityPatches({
        scope,
        actorId,
        feature: 'department_tracking',
        capability: 'EDIT_PROJECTIONS',
        conflictPrefix: 'projectionCues',
        existingItems: asArray<CloudState['projectionCues'][number]>(existingProjectionsData.projectionCues),
        nextItems: localState.projectionCues || [],
    });
}

async function enqueueRunSheetProjectDataWrites(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    baseline: ProjectWriteBaseline,
): Promise<void> {
    const existingPromptBookData = projectResourceData(baseline, RESOURCE_PROMPT_BOOK);
    await enqueueRunSheetEntityWrites(scope, actorId, localState, existingPromptBookData, 'cueAnchors');
    await enqueueRunSheetEntityWrites(scope, actorId, localState, existingPromptBookData, 'customCues');
    await enqueueRunSheetEntityWrites(scope, actorId, localState, projectResourceData(baseline, RESOURCE_FLY), 'flyCues');
}

async function enqueueRunSheetEntityWrites<K extends 'cueAnchors' | 'customCues' | 'flyCues'>(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    existingData: Record<string, unknown>,
    field: K,
): Promise<void> {
    const nextItems = localState[field];
    if (nextItems === undefined) {
        return;
    }

    await enqueueArrayEntityPatches({
        scope,
        actorId,
        feature: 'script_show',
        capability: 'EDIT_RUNSHEET',
        conflictPrefix: field,
        existingItems: asArray<CloudState[K][number]>(existingData[field]),
        nextItems: nextItems || [],
    });
}

async function enqueueProjectTitleIfChanged(
    scope: ProjectScope,
    actorId: string,
    localState: Partial<CloudState>,
    baseline: ProjectWriteBaseline,
): Promise<void> {
    if (
        localState.projectMeta?.title
        && localState.projectMeta.title !== baseline.root.title
    ) {
        await enqueueProjectTitleWrite(scope, actorId, localState.projectMeta.title);
    }
}

async function enqueueProjectTitleWrite(
    scope: ProjectScope,
    actorId: string,
    title: string
): Promise<void> {
    await createAndStoreProjectWriteIntent({
        scope,
        actorId,
        feature: 'setup_access',
        capability: 'EDIT_PROJECT_METADATA',
        kind: 'setField',
        conflictUnit: 'project.title',
        payload: {
            path: 'title',
            value: title,
        },
    });
}

async function enqueueScriptShowStructureReviewWrite(
    scope: ProjectScope,
    actorId: string,
    showStructure: CloudState['showStructure']
): Promise<void> {
    await createAndStoreProjectWriteIntent({
        scope,
        actorId,
        feature: 'script_show',
        capability: 'EDIT_RUNSHEET',
        kind: 'orderedListCommand',
        conflictUnit: 'scriptShow.structure',
        payload: sanitizeDocumentForFirestore({
            operation: 'replaceShowStructure',
            showStructure,
        }),
    });
}

type PeopleCastingEntityInput = {
    scope: ProjectScope;
    actorId: string;
    existingPublicProfiles: PersonnelProfileDoc[];
    existingSensitiveProfiles: PersonnelProfileDoc[];
    actorMember?: ProjectMember;
    personnel?: Personnel[];
};

type ArrayEntityFeature = 'department_tracking' | 'script_show';
type ArrayEntityConflictPrefix =
    | 'props'
    | 'followSpots'
    | 'sets'
    | 'soundClips'
    | 'soundCues'
    | 'micAssignments'
    | 'micSwaps'
    | 'lightingCues'
    | 'followSpotCues'
    | 'cueAnchors'
    | 'customCues'
    | 'flyCues'
    | 'projectionCues';

type ProjectMetaPatchField = 'director' | 'sm' | 'asm' | 'leftLogo' | 'rightLogo';

const PROJECT_META_PATCH_FIELDS: readonly ProjectMetaPatchField[] = ['director', 'sm', 'asm', 'leftLogo', 'rightLogo'];
const SET_PROJECT_IMAGE_FIELDS = ['floorplanImage', 'baseImage', 'bakedImageUrl'] as const;

function setProjectWritePayload(set: CloudState['sets'][number]): CloudState['sets'][number] {
    return omitInlineDataUrlFields(set, SET_PROJECT_IMAGE_FIELDS);
}

async function enqueueRowsReplacementWrite(input: {
    scope: ProjectScope;
    actorId: string;
    existingRows: CloudState['rows'];
    nextRows?: CloudState['rows'];
}): Promise<void> {
    if (input.nextRows === undefined) {
        return;
    }

    const nextRows = input.nextRows || [];
    if (isSameSerializableValue(input.existingRows, nextRows)) {
        return;
    }

    await createAndStoreProjectWriteIntent({
        scope: input.scope,
        actorId: input.actorId,
        feature: 'script_show',
        capability: 'EDIT_RUNSHEET',
        kind: 'orderedListCommand',
        conflictUnit: 'rows.replace',
        payload: sanitizeDocumentForFirestore({
            operation: 'replaceRows',
            rows: nextRows,
        }),
    });
}

async function enqueueMicPackConfigWrite(input: {
    scope: ProjectScope;
    actorId: string;
    existingConfig: unknown;
    nextConfig?: CloudState['micPackConfig'];
}): Promise<void> {
    if (input.nextConfig === undefined) {
        return;
    }

    if (isSameSerializableValue(input.existingConfig, input.nextConfig)) {
        return;
    }

    await createAndStoreProjectWriteIntent({
        scope: input.scope,
        actorId: input.actorId,
        feature: 'department_tracking',
        capability: 'EDIT_SOUND',
        kind: 'patchEntity',
        conflictUnit: 'sound.micPackConfig',
        payload: sanitizeDocumentForFirestore(input.nextConfig),
    });
}

async function enqueueArrayEntityPatches<T extends { id: string }>(input: {
    scope: ProjectScope;
    actorId: string;
    feature: ArrayEntityFeature;
    capability: string;
    conflictPrefix: ArrayEntityConflictPrefix;
    existingItems: T[];
    nextItems: T[];
}): Promise<void> {
    const existingById = new Map(input.existingItems.map((item) => [item.id, item]));

    for (const item of input.nextItems) {
        const existing = existingById.get(item.id);
        if (existing && isSameSerializableValue(comparableArrayEntity(existing), comparableArrayEntity(item))) {
            continue;
        }

        await createAndStoreProjectWriteIntent({
            scope: input.scope,
            actorId: input.actorId,
            feature: input.feature,
            capability: input.capability,
            kind: 'patchEntity',
            conflictUnit: `${input.conflictPrefix}/${item.id}`,
            payload: sanitizeDocumentForFirestore(item),
        });
    }

}

function projectMetaSettingsPatch(
    existingProjectMeta: Partial<CloudState['projectMeta']>,
    nextProjectMeta?: CloudState['projectMeta'],
): Record<ProjectMetaPatchField, string | null> | null {
    if (!nextProjectMeta) {
        return null;
    }

    const patch: Partial<Record<ProjectMetaPatchField, string | null>> = {};
    for (const field of PROJECT_META_PATCH_FIELDS) {
        const nextValue = nextProjectMeta[field];
        if (nextValue === undefined) {
            continue;
        }
        if (existingProjectMeta[field] !== nextValue) {
            patch[field] = nextValue;
        }
    }

    return Object.keys(patch).length > 0
        ? patch as Record<ProjectMetaPatchField, string | null>
        : null;
}

async function enqueueProjectMetaSettingsWrite(input: {
    scope: ProjectScope;
    actorId: string;
    existingProjectMeta: Partial<CloudState['projectMeta']>;
    nextProjectMeta?: CloudState['projectMeta'];
}): Promise<void> {
    const payload = projectMetaSettingsPatch(input.existingProjectMeta, input.nextProjectMeta);
    if (!payload) {
        return;
    }

    await createAndStoreProjectWriteIntent({
        scope: input.scope,
        actorId: input.actorId,
        feature: 'setup_access',
        capability: 'EDIT_PROJECT_METADATA',
        kind: 'patchEntity',
        conflictUnit: 'project.metaSettings',
        payload: sanitizeDocumentForFirestore(payload),
    });
}

async function enqueuePersonnelProfileWrites(input: {
    scope: ProjectScope;
    actorId: string;
    actorMember?: ProjectMember;
    existingPublicProfiles: PersonnelProfileDoc[];
    existingSensitiveProfiles: PersonnelProfileDoc[];
    personnel: Personnel[];
}): Promise<void> {
    const publicById = new Map(input.existingPublicProfiles.map(profile => [profile.id, profile]));
    const sensitiveById = new Map(input.existingSensitiveProfiles.map(profile => [profile.id, profile]));
    for (const person of input.personnel) {
        const publicPatch = changedProfileFields(publicById.get(person.id), toPublicPersonnelProfilePatch(person));
        if (publicPatch) {
            await enqueuePublicPersonnelProfileWrite({
                scope: input.scope,
                actorId: input.actorId,
                actorMember: input.actorMember,
                person,
                patch: publicPatch,
            });
        }

        const sensitivePatch = changedProfileFields(sensitiveById.get(person.id), toSensitivePersonnelProfilePatch(person));
        if (sensitivePatch) {
            await enqueueSensitivePersonnelProfileWrite({
                scope: input.scope,
                actorId: input.actorId,
                actorMember: input.actorMember,
                person,
                patch: sensitivePatch,
            });
        }
    }

}

async function enqueuePeopleCastingEntityWrites(input: PeopleCastingEntityInput): Promise<void> {
    const writes: Promise<void>[] = [];

    if (input.personnel !== undefined) {
        writes.push(enqueuePersonnelProfileWrites({
            scope: input.scope,
            actorId: input.actorId,
            actorMember: input.actorMember,
            existingPublicProfiles: input.existingPublicProfiles,
            existingSensitiveProfiles: input.existingSensitiveProfiles,
            personnel: input.personnel,
        }));
    }

    await Promise.all(writes);
}
